/* THE DEPARTURE WINDOW, solved.
 *
 * ═══ IT DOES NOT RE-ROUTE, AND THAT IS THE HONEST CLAIM ═══
 *
 * The route's GEOMETRY comes from a depth surface that does not change with the
 * clock; what changes with the departure is the TIMING — when you reach each
 * pass, whether it is open, how long you hold. `scheduleFor` answers exactly
 * that, from the same module `autoroute.route()` itself uses for the schedule
 * it shows, so the window and the visible plan cannot disagree about when the
 * boat is where.
 *
 * The alternative is twenty-four full worker solves, which takes seconds and
 * would let the router pick a DIFFERENT PATH at a different hour. That is a
 * real difference and this deliberately does not chase it: the panel says "the
 * same route, solved at every hour", which is what this computes. Re-routing
 * per candidate is a later change with a visible cost, not a silent upgrade.
 *
 * The consequence is that the whole window is instant — no worker, no spinner
 * that matters, and it can be re-run on every speed change without thinking
 * about it.
 *
 * ═══ AND IT GOES THROUGH `planDepartures` ANYWAY ═══
 *
 * Which looks like ceremony around a synchronous loop and is not: it is the
 * seam. The day this does re-route, the solve function becomes a worker call
 * and nothing else in this file, the panel, or the ranking changes.
 */
import { autoroute } from '@gennetay/chart-core/lib/autoroute.svelte';
import {
  candidateDepartures,
  planDepartures,
  rankDepartureWindows,
  type DepartureOutcome,
  type DepartureWindow
} from '@gennetay/chart-core/lib/departure';
import { scheduleFor, waitingMs } from '@gennetay/chart-core/lib/passage';

/** How far ahead to look. Two days is as far as the current model reaches, and
 *  the rows past its edge say so rather than being dropped. */
const WINDOW_MS = 48 * 3_600_000;

class Departures {
  options = $state.raw<DepartureWindow[]>([]);
  done = $state(0);
  total = $state(0);
  #abort: AbortController | null = null;

  /** Why there is nothing to plan, or null. The same rule the layers panel
   *  follows: a panel that silently shows nothing is worse than one that says
   *  why, and both of these are states a user can act on. */
  reason(): string | null {
    if (!autoroute.result || autoroute.result.length < 2) {
      return 'Plan a route first — the window is solved against it.';
    }
    if (!autoroute.useCurrents) {
      return 'Turn on USE CURRENTS. Without a current model every departure takes the same time, so there is nothing to rank.';
    }
    if (!autoroute.model()) {
      return 'No current data on this host, so a departure cannot be timed against the water.';
    }
    return null;
  }

  clear(): void {
    this.#abort?.abort();
    this.options = [];
    this.done = 0;
    this.total = 0;
  }

  async plan(): Promise<void> {
    const pts = autoroute.result;
    const model = autoroute.model();
    if (!pts || pts.length < 2 || !model) {
      this.clear();
      return;
    }

    /* Cancel the run in flight. Changing the boat speed re-asks the whole
       question, and two runs writing into one list interleave their answers. */
    this.#abort?.abort();
    const ac = new AbortController();
    this.#abort = ac;

    const candidates = candidateDepartures(Date.now(), WINDOW_MS);
    this.options = [];
    this.done = 0;
    this.total = candidates.length;

    const raw: DepartureOutcome[] = [];
    await planDepartures(
      candidates,
      async (departT) => {
        const s = scheduleFor(pts, departT, model);
        /* The router's own words, kept. "Stops at leg 3" and "no slack
         * prediction left for Dodd Narrows" are different failures and the
         * skipper needs to know which. */
        const blocked =
          s.impossibleAt !== null
            ? `Stops at leg ${s.impossibleAt + 1} — the stream is stronger than the boat can make good`
            : s.unknownGate
              ? `No slack prediction left for ${s.unknownGate.name}`
              : null;
        return {
          departT,
          arriveT: blocked ? null : s.arriveT,
          waitMs: waitingMs(s),
          /* False past the model's reach: the passage was timed on the narrows
           * and boat speed alone, which is a materially weaker claim and one
           * the row carries. */
          currentsUsed: s.currentsUsed,
          blocked
        };
      },
      {
        signal: ac.signal,
        onresult: (o, done) => {
          if (ac.signal.aborted) return;
          raw.push(o);
          this.done = done;
          /* Re-ranked on every result, so the list is always correct for what
             is known so far rather than jumping when it finishes. */
          this.options = rankDepartureWindows(raw);
        }
      }
    );
  }
}

export const departures = new Departures();
